import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes } from 'react-icons/fa';
import productService from '../../services/productService';
import './FreeMuseOutfitModal.css';

// Muse outfit size chart (measurements in inches): Size -> { bust, waist, hips, length }
const MUSE_OUTFIT_MEASUREMENTS = {
  '2XSMALL': { bust: 33, waist: 29, hips: 33.5, length: 29.5 },
  'XSMALL': { bust: 35, waist: 31, hips: 35.5, length: 30.5 },
  'SMALL': { bust: 37, waist: 33, hips: 37.5, length: 31.5 },
  'MEDIUM': { bust: 39, waist: 34.5, hips: 39.5, length: 32 },
  'LARGE': { bust: 41, waist: 36.5, hips: 41.5, length: 34 }
};
const MUSE_OUTFIT_SIZES = Object.keys(MUSE_OUTFIT_MEASUREMENTS);

const FreeMuseOutfitModal = ({ isOpen, onClose, onConfirm, selectedMuseOutfit: initialSelectedMuseOutfit }) => {
  const [museOutfits, setMuseOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMuseOutfit, setSelectedMuseOutfit] = useState(initialSelectedMuseOutfit || null);
  const [customization, setCustomization] = useState(initialSelectedMuseOutfit?.customization || {
    size: 'MEDIUM',
    bust: MUSE_OUTFIT_MEASUREMENTS.MEDIUM.bust,
    waist: MUSE_OUTFIT_MEASUREMENTS.MEDIUM.waist,
    hips: MUSE_OUTFIT_MEASUREMENTS.MEDIUM.hips,
    length: MUSE_OUTFIT_MEASUREMENTS.MEDIUM.length,
    sizeType: 'adult',
    outfitType: 'full',
    fabricOption: '',
    teamName: '',
    surname: '',
    number: ''
  });

  // Fetch muse outfits
  useEffect(() => {
    const fetchMuseOutfits = async () => {
      try {
        setLoading(true);
        const allProducts = await productService.getAllProducts();
        const museOutfitsList = allProducts.filter(p => 
          p.category?.toLowerCase() === 'muse outfits'
        );
        setMuseOutfits(museOutfitsList);
        
        // If no muse outfit selected and we have outfits, select the first one
        if (!selectedMuseOutfit && museOutfitsList.length > 0) {
          setSelectedMuseOutfit(museOutfitsList[0]);
        }
      } catch (error) {
        console.error('Error fetching muse outfits:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchMuseOutfits();
    }
  }, [isOpen]);

  // Sync measurements when size changes
  const handleSizeChange = (newSize) => {
    const m = MUSE_OUTFIT_MEASUREMENTS[newSize];
    if (m) {
      setCustomization(prev => ({
        ...prev,
        size: newSize,
        bust: m.bust,
        waist: m.waist,
        hips: m.hips,
        length: m.length
      }));
    } else {
      setCustomization(prev => ({ ...prev, size: newSize }));
    }
  };

  // Normalize legacy customization (jerseySize, shortsSize, jerseyType) to new fields
  useEffect(() => {
    if (!isOpen || !initialSelectedMuseOutfit?.customization) return;
    const c = initialSelectedMuseOutfit.customization;
    if (c.size && MUSE_OUTFIT_MEASUREMENTS[c.size]) return; // already new format
    const legacySize = c.jerseySize || c.shortsSize || 'M';
    const mapLegacy = { S: 'SMALL', M: 'MEDIUM', L: 'LARGE', XL: 'LARGE', XXL: 'LARGE' };
    const size = MUSE_OUTFIT_SIZES.includes(legacySize) ? legacySize : (mapLegacy[legacySize] || 'MEDIUM');
    const m = MUSE_OUTFIT_MEASUREMENTS[size];
    setCustomization(prev => ({
      ...prev,
      size,
      bust: m?.bust ?? prev.bust,
      waist: m?.waist ?? prev.waist,
      hips: m?.hips ?? prev.hips,
      length: m?.length ?? prev.length,
      outfitType: c.outfitType ?? c.jerseyType ?? 'full'
    }));
  }, [isOpen, initialSelectedMuseOutfit?.customization]);

  // Parse fabric surcharges
  const fabricSurcharges = useMemo(() => {
    if (!selectedMuseOutfit?.fabric_surcharges) return {};
    try {
      const parsed = typeof selectedMuseOutfit.fabric_surcharges === 'string'
        ? JSON.parse(selectedMuseOutfit.fabric_surcharges)
        : selectedMuseOutfit.fabric_surcharges;
      return parsed || {};
    } catch (error) {
      console.warn('Failed to parse fabric surcharges:', error);
      return {};
    }
  }, [selectedMuseOutfit]);

  const fabricOptions = Object.keys(fabricSurcharges).length > 0 
    ? Object.keys(fabricSurcharges)
    : ['Polydex', 'Microcool', 'Aircool', 'Drifit', 'Square Mesh', 'Lacoste'];

  const handleConfirm = () => {
    if (!selectedMuseOutfit) return;
    
    const finalFabricOption = customization.fabricOption || (fabricOptions.length > 0 ? fabricOptions[0] : '');
    const outfitType = customization.outfitType ?? customization.jerseyType ?? 'full';
    const size = customization.size || 'MEDIUM';
    const m = MUSE_OUTFIT_MEASUREMENTS[size] || {};
    
    const museOutfitData = {
      product: selectedMuseOutfit,
      customization: {
        ...customization,
        fabricOption: finalFabricOption,
        outfitType,
        jerseyType: outfitType,
        size,
        bust: customization.bust ?? m.bust,
        waist: customization.waist ?? m.waist,
        hips: customization.hips ?? m.hips,
        length: customization.length ?? m.length
      }
    };
    
    onConfirm(museOutfitData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="free-muse-outfit-modal-overlay" onClick={onClose}>
      <div className="free-muse-outfit-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="free-muse-outfit-modal-header">
          <h2>Choose Your Free Muse Outfit</h2>
          <button className="free-muse-outfit-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="free-muse-outfit-modal-body">
          {loading ? (
            <div className="free-muse-outfit-loading">Loading muse outfits...</div>
          ) : museOutfits.length === 0 ? (
            <div className="free-muse-outfit-empty">No muse outfits available</div>
          ) : (
            <>
              {/* Product Selection */}
              <div className="free-muse-outfit-selection">
                <label>Select Muse Outfit:</label>
                <div className="free-muse-outfit-grid">
                  {museOutfits.map(outfit => (
                    <div
                      key={outfit.id}
                      className={`free-muse-outfit-card ${selectedMuseOutfit?.id === outfit.id ? 'selected' : ''}`}
                      onClick={() => setSelectedMuseOutfit(outfit)}
                    >
                      <img 
                        src={outfit.main_image || outfit.image_url || '/placeholder.jpg'} 
                        alt={outfit.name}
                        className="free-muse-outfit-image"
                      />
                      <div className="free-muse-outfit-name">{outfit.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedMuseOutfit && (
                <div className="free-muse-outfit-customization">
                  <h3>Customize Your Muse Outfit</h3>

                  {/* Size Type */}
                  <div className="free-muse-outfit-field">
                    <label>Size Type:</label>
                    <div className="free-muse-outfit-buttons">
                      <button
                        type="button"
                        className={customization.sizeType === 'adult' ? 'active' : ''}
                        onClick={() => setCustomization(prev => ({ ...prev, sizeType: 'adult' }))}
                      >
                        Adult
                      </button>
                      <button
                        type="button"
                        className={customization.sizeType === 'kids' ? 'active' : ''}
                        onClick={() => setCustomization(prev => ({ ...prev, sizeType: 'kids' }))}
                      >
                        Kids
                      </button>
                    </div>
                  </div>

                  {/* Outfit Type */}
                  <div className="free-muse-outfit-field">
                    <label>Outfit Type:</label>
                    <div className="free-muse-outfit-buttons">
                      <button
                        type="button"
                        className={(customization.outfitType || customization.jerseyType) === 'full' ? 'active' : ''}
                        onClick={() => setCustomization(prev => ({ ...prev, outfitType: 'full', jerseyType: 'full' }))}
                      >
                        Full Set
                      </button>
                      <button
                        type="button"
                        className={(customization.outfitType || customization.jerseyType) === 'shirt' ? 'active' : ''}
                        onClick={() => setCustomization(prev => ({ ...prev, outfitType: 'shirt', jerseyType: 'shirt' }))}
                      >
                        Shirt Only
                      </button>
                      <button
                        type="button"
                        className={(customization.outfitType || customization.jerseyType) === 'shorts' ? 'active' : ''}
                        onClick={() => setCustomization(prev => ({ ...prev, outfitType: 'shorts', jerseyType: 'shorts' }))}
                      >
                        Skirts Only
                      </button>
                    </div>
                  </div>

                  {/* Muse outfit size (replaces shirt size & skirt size) */}
                  <div className="free-muse-outfit-field">
                    <label>Size:</label>
                    <select
                      value={customization.size || 'MEDIUM'}
                      onChange={(e) => handleSizeChange(e.target.value)}
                    >
                      {MUSE_OUTFIT_SIZES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  {/* Measurements (inches) - from size chart */}
                  <div className="free-muse-outfit-measurements">
                    <label>Measurements (inches)</label>
                    <div className="free-muse-outfit-measurements-grid">
                      <div className="free-muse-outfit-measure">
                        <span className="free-muse-outfit-measure-label">Bust</span>
                        <span className="free-muse-outfit-measure-value">{customization.bust ?? MUSE_OUTFIT_MEASUREMENTS[customization.size || 'MEDIUM']?.bust ?? '—'}"</span>
                      </div>
                      <div className="free-muse-outfit-measure">
                        <span className="free-muse-outfit-measure-label">Waist</span>
                        <span className="free-muse-outfit-measure-value">{customization.waist ?? MUSE_OUTFIT_MEASUREMENTS[customization.size || 'MEDIUM']?.waist ?? '—'}"</span>
                      </div>
                      <div className="free-muse-outfit-measure">
                        <span className="free-muse-outfit-measure-label">Hips</span>
                        <span className="free-muse-outfit-measure-value">{customization.hips ?? MUSE_OUTFIT_MEASUREMENTS[customization.size || 'MEDIUM']?.hips ?? '—'}"</span>
                      </div>
                      <div className="free-muse-outfit-measure">
                        <span className="free-muse-outfit-measure-label">Length</span>
                        <span className="free-muse-outfit-measure-value">{customization.length ?? MUSE_OUTFIT_MEASUREMENTS[customization.size || 'MEDIUM']?.length ?? '—'}"</span>
                      </div>
                    </div>
                  </div>

                  {/* Fabric Option */}
                  {fabricOptions.length > 0 && (
                    <div className="free-muse-outfit-field">
                      <label>Fabric Option:</label>
                      <select
                        value={customization.fabricOption || fabricOptions[0]}
                        onChange={(e) => setCustomization(prev => ({ ...prev, fabricOption: e.target.value }))}
                      >
                        {fabricOptions.map(fabric => (
                          <option key={fabric} value={fabric}>
                            {fabric} {fabricSurcharges[fabric] > 0 ? `(+₱${fabricSurcharges[fabric]})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Team Name / Organization */}
                  <div className="free-muse-outfit-field">
                    <label>Team Name / Organization:</label>
                    <input
                      type="text"
                      value={customization.teamName}
                      onChange={(e) => setCustomization(prev => ({ ...prev, teamName: e.target.value }))}
                      placeholder="Enter team name or organization"
                    />
                  </div>

                  {/* Surname */}
                  <div className="free-muse-outfit-field">
                    <label>Surname:</label>
                    <input
                      type="text"
                      value={customization.surname}
                      onChange={(e) => setCustomization(prev => ({ ...prev, surname: e.target.value }))}
                      placeholder="Enter surname"
                    />
                  </div>

                  {/* Jersey Number */}
                  <div className="free-muse-outfit-field">
                    <label>Jersey Number:</label>
                    <input
                      type="text"
                      value={customization.number}
                      onChange={(e) => setCustomization(prev => ({ ...prev, number: e.target.value }))}
                      placeholder="Enter jersey number"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="free-muse-outfit-modal-footer">
          <button className="free-muse-outfit-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="free-muse-outfit-confirm-btn" 
            onClick={handleConfirm}
            disabled={!selectedMuseOutfit}
          >
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeMuseOutfitModal;

